// AskPsdPageV2 — redesigned conversational UI, built as a fully separate
// component so the original AskPsdPage can be restored by simply pointing
// the route/dropdown back at it (same pattern EMD uses for AskDevLens v2/v3).

import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { TextInput, Icon, Font } from 'pepsico-ds';
import Header from '../../../containers/LayoutV2/Header';
import { createAskPsdApi } from '../services/api';
import { normalizeApiResponse } from '../services/responseNormalizer';
import { logger } from '../services/logger';
import { useSessionHistory } from './useSessionHistory';
import SessionSidebar from './SessionSidebar';
import AnswerBlock from './AnswerBlock';
import '../askPsd.css';
import './askPsdV2.css';

const POLL_INTERVAL_MS = 1000;
const TERMINAL_STATUSES = ['SUCCESS', 'ERROR'];
const MAX_CONSECUTIVE_POLL_FAILURES = 5;
// Hard cap on how long a single question can poll for — protects against a
// job that's stuck RUNNING forever from polling (and showing "Processing…")
// indefinitely.
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;

let turnIdCounter = 0;
const nextTurnId = () => `turn-${++turnIdCounter}`;

export default function AskPsdPageV2() {
  const { oktaAuth } = useOktaAuth();
  const api = useMemo(
    () => createAskPsdApi(() => oktaAuth?.getAccessToken()),
    [oktaAuth]
  );
  const sessionHistory = useSessionHistory(api);

  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [turns, setTurns] = useState([]);
  const [showPrompts, setShowPrompts] = useState(false);
  const pollTimer = useRef(null);
  const latestTurnRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (turns.length > 0) {
      latestTurnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [turns.length]);

  // Cancel any in-flight request and pending poll when the page unmounts
  // (e.g. user navigates away) so we don't call setState on an unmounted
  // component or leave a request hanging.
  useEffect(() => {
    return () => {
      if (pollTimer.current) {
        clearTimeout(pollTimer.current);
      }
      abortControllerRef.current?.abort();
    };
  }, []);

  const stopPolling = () => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  };

  const resetConversation = () => {
    stopPolling();
    abortControllerRef.current?.abort();
    setLoading(false);
    setTurns([]);
    setQuestion('');
    setShowPrompts(false);
    sessionHistory.startNewSession();
  };

  const handleResumeSession = async (sessionId) => {
    stopPolling();
    abortControllerRef.current?.abort();
    setLoading(false);
    setQuestion('');
    setShowPrompts(false);
    try {
      const historyTurns = await sessionHistory.resumeSession(sessionId);
      setTurns(historyTurns);
    } catch (err) {
      logger.error('Failed to resume session', err);
    }
  };

  // Deleting the session currently shown on screen should clear the thread
  // too — otherwise the (now-deleted) conversation stays visible even
  // though it no longer exists in history.
  const handleDeleteSession = async (sessionId) => {
    const wasActive = sessionHistory.activeSessionId === sessionId;
    await sessionHistory.deleteSession(sessionId);
    if (wasActive) {
      stopPolling();
      abortControllerRef.current?.abort();
      setLoading(false);
      setTurns([]);
    }
  };

  const updateTurn = (turnId, patch) => {
    setTurns((prev) => prev.map((t) => (t.id === turnId ? { ...t, ...patch } : t)));
  };

  const submit = async (event) => {
    event?.preventDefault();

    if (!question.trim() || loading) {
      return;
    }

    const turnId = nextTurnId();
    const askedQuestion = question.trim();
    setTurns((prev) => [...prev, { id: turnId, question: askedQuestion, steps: [], statusMsg: 'Submitting question…' }]);
    setQuestion('');

    const controller = new AbortController();
    abortControllerRef.current = controller;
    const pollStartedAt = Date.now();

    try {
      setLoading(true);
      const sessionArg = sessionHistory.activeSessionId
        ? { sessionId: sessionHistory.activeSessionId }
        : { sessionTitle: askedQuestion };
      const { jobId, sessionId } = await api.askQuestion(askedQuestion, sessionArg, controller.signal);
      sessionHistory.registerSession(sessionId, askedQuestion);
      updateTurn(turnId, { statusMsg: 'Processing…' });

      let consecutiveFailures = 0;

      const poll = async () => {
        if (Date.now() - pollStartedAt > MAX_POLL_DURATION_MS) {
          stopPolling();
          setLoading(false);
          updateTurn(turnId, {
            statusMsg: undefined,
            error: 'This question is taking longer than expected. Please try again.',
          });
          return;
        }

        try {
          const data = await api.pollStatus(jobId, controller.signal);
          consecutiveFailures = 0;
          const status = (data.status || '').toUpperCase();

          const running = Array.isArray(data.steps)
            ? data.steps.find((s) => (s.status || '').toUpperCase() === 'RUNNING')
            : null;

          updateTurn(turnId, {
            steps: data.steps || [],
            statusMsg: running ? running.name : undefined,
          });

          if (TERMINAL_STATUSES.includes(status)) {
            stopPolling();
            setLoading(false);

            if (status === 'SUCCESS') {
              if (data.result?.status === 'CLARIFICATION_REQUIRED') {
                updateTurn(turnId, {
                  statusMsg: undefined,
                  error: data.result.clarificationQuestion || 'Could you clarify your question?',
                });
              } else {
                updateTurn(turnId, {
                  statusMsg: undefined,
                  response: normalizeApiResponse(data.result ?? data),
                });
              }
            } else {
              updateTurn(turnId, {
                statusMsg: undefined,
                error: `Job ${status.toLowerCase()}: ${data.errorMessage || 'Unknown error'}`,
              });
            }
          } else {
            pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
          }
        } catch (pollError) {
          if (controller.signal.aborted) {
            return;
          }
          consecutiveFailures += 1;
          logger.error(`Poll attempt failed (${consecutiveFailures}/${MAX_CONSECUTIVE_POLL_FAILURES})`, pollError);

          if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
            stopPolling();
            setLoading(false);
            updateTurn(turnId, { statusMsg: undefined, error: 'Polling failed after several retries. Please try again.' });
          } else {
            updateTurn(turnId, { statusMsg: 'Reconnecting…' });
            pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
          }
        }
      };

      pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
    } catch (submitError) {
      if (controller.signal.aborted) {
        return;
      }
      logger.error('Failed to submit question', submitError);
      setLoading(false);
      updateTurn(turnId, {
        statusMsg: undefined,
        error: submitError instanceof Error && submitError.name === 'AskPsdApiError'
          ? submitError.message
          : 'I could not submit the question. Please check the API and try again.',
      });
    }
  };

  return (
    <>
      <Header />
      <div className="ask-psd-shell ask-psd-v2-shell">
        <button
          type="button"
          className="ask-psd-v2-home-btn"
          // Full page load (not React Router's navigate) — the Dashboard
          // route renders blank when reached via client-side navigation
          // from here, but works fine on a fresh load.
          onClick={() => {
            window.location.href = '/';
          }}
          aria-label="Go to homepage"
          title="Go to homepage"
        >
          <Icon icon="home" size="medium" />
        </button>
        <div className="ask-psd-v2-layout">
          <SessionSidebar
            sessions={sessionHistory.sessions}
            activeSessionId={sessionHistory.activeSessionId}
            onResume={handleResumeSession}
            onDelete={handleDeleteSession}
            onNew={resetConversation}
            loading={sessionHistory.sessionsLoading}
            error={sessionHistory.sessionsError}
          />
          <div className="ask-psd-v2-container">
          <div className="ask-psd-v2-topbar">
            <div>
              <Font headingLevel={4}>Ask PSD</Font>
              <Font size="small" textColorClass="ask-psd-v2-subtitle">
                Ask business questions in plain English and get summaries, charts, SQL, and recommendations.
              </Font>
            </div>
            {turns.length > 0 && (
              <button type="button" className="ask-psd-v2-new-chat" onClick={resetConversation}>
                New chat
              </button>
            )}
          </div>

          <form className="ask-psd-v2-inputbar" onSubmit={submit}>
            <div className="ask-psd-textinput">
              <div className="ask-psd-v2-prompt-popover-anchor">
                <button
                  type="button"
                  className="ask-psd-v2-bulb"
                  onClick={() => setShowPrompts((v) => !v)}
                  aria-expanded={showPrompts}
                  aria-label="Example prompts"
                  title="Example prompts"
                >
                  💡
                </button>
                {showPrompts && (
                  <div className="ask-psd-v2-prompt-popover">
                    <span
                      onClick={() => {
                        setQuestion('Show usage duration trend by planning role for the last 6 months.');
                        setShowPrompts(false);
                      }}
                    >
                      Show usage duration trend by planning role for the last 6 months.
                    </span>
                    <span
                      onClick={() => {
                        setQuestion('Compare fillrate improvement by category and restech.');
                        setShowPrompts(false);
                      }}
                    >
                      Compare fillrate improvement by category and restech.
                    </span>
                  </div>
                )}
              </div>
              <TextInput
                label="Ask a question"
                value={question}
                onUpdate={setQuestion}
                placeholder="Show usage duration trend by planning role for the last 6 months."
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              className="ask-psd-send"
              disabled={loading || !question.trim()}
              aria-label="Send"
            >
              <Icon icon="send" size="medium" />
            </button>
          </form>

          {turns.length > 0 && (
            <div className="ask-psd-v2-thread">
              {turns.map((turn, index) => (
                <div
                  key={turn.id}
                  className="ask-psd-v2-turn"
                  ref={index === turns.length - 1 ? latestTurnRef : null}
                >
                  <div className="ask-psd-v2-bubble">{turn.question}</div>
                  <AnswerBlock turn={turn} />
                </div>
              ))}
            </div>
          )}
          </div>
        </div>
      </div>
    </>
  );
}
